import { useCallback, useEffect } from 'react';

import {
  CustomerBalanceTransactionTypeEnum,
  CustomerLedgerUpdateTypeEnum,
  formattedDateForSubscription,
  NotificationVariants,
  useCallbackRef,
  useGlobalToasts,
  useMarketAccountsContext,
} from '@talos/kyoko';

import { useOnlineTransferMarkets } from '@src/containers/Transfers';
import { useFeatureFlag } from '@src/hooks';
import { useCustomersContext } from '@src/hooks/useCustomer';
import { useCustomerBalance } from '@src/providers';
import type {
  CustomerBalanceTransactionDrawerProps,
  CustomerBalanceTransactionDrawerState,
  CustomerTransactionForm,
} from '../types';
import { FORCE_WITHDRAW_CONFIRM_TEXT } from '../utils';
import { useTransferOptions } from './useTransferOptions';

interface PreviewDisclosureContext {
  form: CustomerTransactionForm;
  isForceWithdraw: boolean;
}

interface PreviewDisclosure {
  open: (context: PreviewDisclosureContext) => void;
  context?: PreviewDisclosureContext;
}

interface UseTransactionSubmissionProps extends Pick<
  CustomerBalanceTransactionDrawerProps,
  'onClose' | 'onConfirmedOverride'
> {
  drawerState: CustomerBalanceTransactionDrawerState;
  updateDrawerState: (updates: Partial<CustomerBalanceTransactionDrawerState>) => void;
  confirmText: string;
  effectiveForceWithdraw: boolean;
}

export function useTransactionSubmission({
  drawerState,
  updateDrawerState,
  confirmText,
  effectiveForceWithdraw,
  onClose,
  onConfirmedOverride,
}: UseTransactionSubmissionProps) {
  const { customerBalanceRequireForceWithdrawConfirm } = useFeatureFlag();
  const providerOptions = useOnlineTransferMarkets();
  const { getEffectiveLinkForCustomerTransaction } = useCustomersContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { add: addToast } = useGlobalToasts();
  const customerBalanceService = useCustomerBalance();
  const { sourceOptions, destinationOptions } = useTransferOptions({ drawerState });

  const updateFormUsingEffectiveLink = useCallbackRef((drawerState: CustomerBalanceTransactionDrawerState) => {
    if (drawerState.customer && drawerState.currency && drawerState.customerAccount && drawerState.transferType) {
      updateDrawerState({ source: undefined, destination: undefined });
      getEffectiveLinkForCustomerTransaction({
        Currency: drawerState.currency,
        MarketAccount: drawerState.customerAccount,
        TransactionType:
          drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw
            ? CustomerBalanceTransactionTypeEnum.Withdrawal
            : CustomerBalanceTransactionTypeEnum.Deposit,
      }).then(({ data }) => {
        if (data == null || data.length === 0) {
          return;
        }
        const { SourceMarketAccount, DestinationMarketAccount } = data[0];
        const provider = SourceMarketAccount ? marketAccountsByName.get(SourceMarketAccount)?.Market : undefined;
        // If we cannot find the provider, it does not make sense to populate the source and destination
        if (provider && providerOptions.map(option => option.Name).includes(provider)) {
          updateDrawerState({ provider });
          if (SourceMarketAccount && sourceOptions.find(option => option.marketAccount.Name === SourceMarketAccount)) {
            updateDrawerState({ source: SourceMarketAccount });
          }
          if (
            DestinationMarketAccount &&
            destinationOptions.find(option => option.marketAccount.Name === DestinationMarketAccount)
          ) {
            updateDrawerState({ destination: DestinationMarketAccount });
          }
        }
      });
    }
  });

  useEffect(() => {
    updateFormUsingEffectiveLink({
      customer: drawerState.customer,
      currency: drawerState.currency,
      customerAccount: drawerState.customerAccount,
      transferType: drawerState.transferType,
      transferMethod: drawerState.transferMethod,
    });
  }, [
    drawerState.customer,
    drawerState.currency,
    drawerState.customerAccount,
    drawerState.transferType,
    updateFormUsingEffectiveLink,
    drawerState.transferMethod,
  ]);

  // Transform state to CustomerTransactionForm format
  const createFormFromState = useCallback((): CustomerTransactionForm => {
    const baseForm: CustomerTransactionForm = {
      Counterparty: drawerState.customer,
      MarketAccount: drawerState.customerAccount,
      Type: drawerState.transferType,
      Currency: drawerState.currency,
      Amount: drawerState.amount,
      Comments: drawerState.internalComments,
      ExternalComments: drawerState.externalComments,
      ExternalID: drawerState.externalID,
    };

    // For manual transfers, only include TxHash if provided (no transfer automation fields)
    if (drawerState.transferMethod === 'manual') {
      if (drawerState.txHash) {
        baseForm.TxHash = drawerState.txHash;
      }
    } else {
      // For initiate transfers, include transfer automation fields but not TxHash
      baseForm.FromMarketAccount = drawerState.source;
      baseForm.ToMarketAccount = drawerState.destination;
      // If TransferCurrency is not set, use the Currency
      baseForm.TransferCurrency = drawerState.aliasCurrency ?? drawerState.currency;

      // Only include TxHash for deposits (user provides existing tx hash)
      if (drawerState.transferType === CustomerLedgerUpdateTypeEnum.Deposit && drawerState.txHash) {
        baseForm.TxHash = drawerState.txHash;
      }
    }

    return baseForm;
  }, [drawerState]);

  const handlePreviewTransaction = useCallback(
    (previewDisclosure: PreviewDisclosure) => {
      return () => {
        if (
          effectiveForceWithdraw &&
          customerBalanceRequireForceWithdrawConfirm &&
          confirmText !== FORCE_WITHDRAW_CONFIRM_TEXT
        ) {
          addToast({
            text: `Please type "${FORCE_WITHDRAW_CONFIRM_TEXT}" to confirm force withdraw`,
            variant: NotificationVariants.Negative,
          });
          return;
        }
        const form = createFormFromState();
        previewDisclosure.open({ form, isForceWithdraw: effectiveForceWithdraw });
      };
    },
    [effectiveForceWithdraw, customerBalanceRequireForceWithdrawConfirm, confirmText, addToast, createFormFromState]
  );

  const handleConfirmTransaction = useCallback(
    (previewDisclosure: PreviewDisclosure) => {
      return async () => {
        if (!previewDisclosure.context) {
          return;
        }

        const { form, isForceWithdraw } = previewDisclosure.context;

        // Calculate isManualTransfer consistently
        const isManualTransfer =
          drawerState.transferMethod === 'manual' && drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw;

        try {
          if (onConfirmedOverride) {
            await onConfirmedOverride(form, isManualTransfer);
            return;
          }
          // Default to confirmBalanceTransaction if onConfirmed is not provided
          await confirmBalanceTransaction({
            form,
            isForceWithdraw,
            isManualTransfer,
            customerBalanceService,
            addToast,
          });

          onClose();
        } catch (e: unknown) {
          addToast({
            text: e instanceof Error ? e.message : 'Could not add new balance transaction.',
            variant: NotificationVariants.Negative,
          });
        }
      };
    },
    [
      onConfirmedOverride,
      drawerState.transferMethod,
      drawerState.transferType,
      customerBalanceService,
      addToast,
      onClose,
    ]
  );

  return {
    handlePreviewTransaction,
    handleConfirmTransaction,
  };
}

interface ConfirmBalanceTransactionParams {
  form: CustomerTransactionForm;
  isForceWithdraw: boolean;
  isManualTransfer: boolean;
  customerBalanceService: ReturnType<typeof useCustomerBalance>;
  addToast: ReturnType<typeof useGlobalToasts>['add'];
}

async function confirmBalanceTransaction({
  form,
  isForceWithdraw,
  isManualTransfer,
  customerBalanceService,
  addToast,
}: ConfirmBalanceTransactionParams) {
  if (form.Type === CustomerLedgerUpdateTypeEnum.Set) {
    if (!form.ExternalID || !form.Counterparty || !form.Currency || !form.Amount || !form.MarketAccount) {
      addToast({
        text: 'Fields ExternalID, Counterparty, Currency, Amount, and MarketAccount are required to Set a customer balance.',
        variant: NotificationVariants.Negative,
      });
      return;
    }
    await customerBalanceService.updateCustomerBalance({
      ExternalID: form.ExternalID,
      Counterparty: form.Counterparty,
      Currency: form.Currency,
      Amount: form.Amount,
      MarketAccount: form.MarketAccount,
      ...(form.TxHash ? { TxHash: form.TxHash } : {}),
      ...(form.Comments ? { Comments: form.Comments } : {}),
      ...(form.ExternalComments ? { ExternalComments: form.ExternalComments } : {}),
    });

    addToast({
      text: 'Successfully updated customer balance.',
      variant: NotificationVariants.Positive,
    });
    return;
  }

  // Convert form to the format expected by the API - filter out TransactTime if it's a Date
  const { TransactTime, ...formWithoutTransactTime } = form;
  const transactionTime = typeof TransactTime === 'string' ? new Date(TransactTime) : TransactTime;
  const apiForm = {
    ...formWithoutTransactTime,
    ...(TransactTime ? { TransactTime: formattedDateForSubscription(transactionTime) } : {}),
  };

  await customerBalanceService.createBalanceTransaction(apiForm, isForceWithdraw, isManualTransfer);

  addToast({
    text: 'Successfully created new balance transaction.',
    variant: NotificationVariants.Positive,
  });
}
